import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {BpmnExtensionHelper} from '@ispm/shared/bpmn-io/bpmn-extension-helper';
import {
  connection,
  END_NODE_ID,
  exclusiveGatewayType,
  HEAD_NODE_ID,
  subProcessType,
  taskType,
  trackingChangeExtensionType
} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {Base, Connection, Shape} from 'diagram-js/lib/model';
import {is} from 'bpmn-js/lib/util/ModelUtil';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {clone} from 'lodash';
import {selectFloatedNodesMap} from "@ispm-client/modules/bpmn-modeler/store/selectors/floating-nodes.selector";
import {State} from "@ispm-client/modules/bpmn-modeler/store/state";
import {select, Store} from '@ngrx/store';
import {take} from 'rxjs/operators';
import {CommandType} from "@ispm/shared/bpmn-io/bpmn-js";

export class ProcessBpmnForApprovalHandler implements CommandHandler {

  private deletedTrackingChangeElements: Array<Base> = [];
  private readyForPreview: boolean;
  private backupTrackingChangesMap: Map<string, Array<string>> = new Map<string, Array<string>>();

  constructor(private bpmnExtensionHelper: BpmnExtensionHelper, private elementRegistry,
              private pathwaysModeling: PathwaysModeling, private store$: Store<State>, private commandStack) {
  }

  postExecute(context): any {
    const finalDeployment = context.finalDeployment;
    this.readyForPreview = context.readyForPreview;
    if (finalDeployment || this.readyForPreview) {
      const rootElement = this.elementRegistry.get(HEAD_NODE_ID);
      if (rootElement) {
        this.cleanupTrackingChangesData(rootElement);
        this.backupTrackingChangesData();
      }
    }
  }

  private backupTrackingChangesData() {
    this.commandStack.execute(CommandType.BACKUP_AND_RESTORE_BPMN_BEFORE_APPROVAL, {backupTrackingChanges: this.backupTrackingChangesMap});
    this.backupTrackingChangesMap = new Map<string, Array<string>>();
  }

  private cleanupTrackingChangesData(rootElement) {
    this.markTrackingChangesData(rootElement);
    this.markTrackingChangeDataFromFloatedGateways();
    this.removeDeletedTrackingChangeElements();
  }


  //if delete tracking change exists, remove the element
  //if status is approve/publish , backup tracking change type in case of failure we will able to restore them
  private handleTrackingChangeForElements(element: Shape | Connection) {
    const trackingChange = ModelingUtil.getTrackingChange(element);
    if (trackingChange) {
      const trackingChangeType = trackingChange.type;
      if (trackingChangeType === 'deleted') {
        this.deletedTrackingChangeElements.push(element);
      } else {
        if (!this.readyForPreview) {
          this.backupTrackingChangesForElements(element.id, trackingChangeType);
          this.bpmnExtensionHelper.removeExtension(element, trackingChangeExtensionType);
        }
      }
    }
  }

  private backupTrackingChangesForElements(elementID: string, trackingChangeType: string) {
    if (this.backupTrackingChangesMap.has(trackingChangeType)) {
      let trackingChangesNodes = this.backupTrackingChangesMap.get(trackingChangeType);
      trackingChangesNodes.push(elementID);
      this.backupTrackingChangesMap.set(trackingChangeType, trackingChangesNodes);
    } else {
      this.backupTrackingChangesMap.set(trackingChangeType, [elementID]);
    }

  }

  private removeDeletedTrackingChangeElements(): void {
    this.deletedTrackingChangeElements.forEach((deletedTrackingChangeElement) => {
      if (ModelingUtil.isConnection(deletedTrackingChangeElement)) {
        this.pathwaysModeling.deleteConnection(deletedTrackingChangeElement, true);
      } else {
        this.pathwaysModeling.removeNode(deletedTrackingChangeElement, true);
      }
    });
    // in case deleting gateway it could deleting connections that may no be found after in elementRegistry
    this.deletedTrackingChangeElements = this.deletedTrackingChangeElements.filter(element => this.elementRegistry.get(element.id) !== undefined);
    this.pathwaysModeling.removeElements(this.deletedTrackingChangeElements);
    this.deletedTrackingChangeElements = [];
    this.removeFloatedDeletedTrackingChangeElements();
  }

  private removeFloatedDeletedTrackingChangeElements(): void {
    this.store$.pipe(select(selectFloatedNodesMap), take(1)).subscribe(floatedNodesMap => {
      if (floatedNodesMap) {
        floatedNodesMap.forEach((floatedNodes, layer) => {
          const floatedNodesPerLayer = floatedNodesMap.get(layer);
          const floatedNodesToBeRemoved = floatedNodesPerLayer.filter((floatedNodeID) => this.elementRegistry.get(floatedNodeID) !== undefined &&
            ModelingUtil.hasDeletionTrackingChange(this.elementRegistry.get(floatedNodeID))).map(floatedNodeIDToBeRemoved => this.elementRegistry.get(floatedNodeIDToBeRemoved));
          this.pathwaysModeling.removeElements(floatedNodesToBeRemoved);
        });
      }
    });
  }

  private markTrackingChangesData(element: Shape | Connection, visited?: Set<Shape | Connection>) {
    if (! visited) {
      visited = new Set();
    } else if (visited.has(element)) {
      return;
    }
    visited.add(element);
    if (is(element, taskType) || is(element, subProcessType) || is(element, connection) || is(element, exclusiveGatewayType)) {
      this.handleTrackingChangeForElements(element);
    }
    // need to clone the connection array, in order not to modify the shapes connections
    if (element.outgoing) {
      const outgoingConnections: Array<Connection> = clone(element.outgoing);
      while (outgoingConnections.length > 0) {
        const connection = outgoingConnections.pop();
        this.handleTrackingChangeForElements(connection);
        const target = connection.target;
        this.markTrackingChangesData(target, visited);
      }
    }
  }

  private markTrackingChangeDataFromFloatedGateways() {
    let gatewaysToDelete = new Set();
    const endNode = this.elementRegistry.get(END_NODE_ID);
    if (endNode) {
      ModelingUtil.getSourcesByTypeFromTargets(exclusiveGatewayType, [endNode], gatewaysToDelete);
      Array.from(gatewaysToDelete).forEach((gateway: Shape) => {
        this.bpmnExtensionHelper.markElementForChange(gateway, 'deleted');
        this.markTrackingChangesData(gateway);
      });
    }
  }
}
